import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { downloadUrl } from '../../lib/files.js'
import { formatDate, formatDateTime, money } from '../../lib/format.js'
import { useJobOverlay } from '../../lib/job-overlay.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../../lib/trpc.js'
import { Icon } from '../Icon.js'
import { Button, Spinner, Textarea, toneStyles } from '../ui.js'
import { AddServiceSheet } from './AddServiceSheet.js'
import { Label } from './panes.js'
import {
  reportLine,
  serviceIcon,
  serviceStatus,
  ticketBadge,
  ticketDetail,
  type JobServiceRow,
  type ReportStage,
} from './services.js'
import type { BriefHazards, BriefHistory, BriefTickets, Job } from './types.js'

type JobReports = RouterOutputs['forms']['jobReports']

/** A completed report's stored PDF, as the email window attaches it. */
export type ReportPdf = { attachmentId: string; filename: string; serviceName: string }

/**
 * The services on this job, each one the report it produces.
 *
 * The primary block on the Work tab. A job at one address is rarely one piece
 * of work: the inspection, the cockroach treatment and the rat stations happen
 * on one visit, are quoted as separate lines, and each produces its own signed
 * report. ServiceM8 could only say "one job, one category", which is why 660
 * characters of description ended up carrying five jobs' worth of
 * instructions. Decision 53.
 *
 * **A service and its report are one card** (the 2026-10-09 amendment to ADR
 * 029). The card says where the report is — not started, "In progress · 4 of
 * 17", "Report complete · 10:16 am" — and its button is the next thing to do
 * with it: Start report, Continue report, or the PDF and Email to client once
 * it is signed. There is no second step inside the card to reach the report.
 * The brief a technician reads at the gate (scope, the last notes here,
 * safety, tickets, notes on this service) is still behind the card's name, and
 * is fetched when it opens, because five closed cards would otherwise pay for
 * five site-wide note searches.
 *
 * Under the cards, **Last time here** is the last visit that left a report at
 * this address: when, who, and one line per report from the form's own
 * `recap`. It is read, not suggested; nothing is filled in from it.
 *
 * The legacy checklist is still on the tab, below and labelled as the old
 * system's. Nothing is back-parsed from it into a service.
 */
export function ServicesPane({
  job,
  canEdit,
  isAddingNote,
  onAddNote,
  onRemoveService,
  onEmailReport,
  field = false,
}: {
  job: Job
  canEdit: boolean
  isAddingNote: boolean
  onAddNote: (jobServiceId: string, body: string, done: () => void) => void
  onRemoveService: (jobServiceId: string) => void
  /** Opens the email window with a signed report attached. Absent hides the button. */
  onEmailReport?: (pdf: ReportPdf) => void
  /** The field app's spacing and heading. Decision 93. */
  field?: boolean
}) {
  const trpc = useTRPC()
  const { jobServiceId: reportOpen } = useJobOverlay()
  const [open, setOpen] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const services = job.services ?? []
  const done = services.filter((service) => serviceStatus(service).done).length
  const reports = useQuery({
    ...trpc.forms.jobReports.queryOptions({ jobId: job.id }),
    enabled: services.length > 0,
  })

  // A report saves as it is typed in, without telling the card. When it
  // closes, the card asks again, so "4 of 17" is where it was left.
  const wasOpen = useRef(reportOpen)
  const { refetch } = reports
  useEffect(() => {
    if (wasOpen.current !== null && reportOpen === null) void refetch()
    wasOpen.current = reportOpen
  }, [reportOpen, refetch])

  return (
    <section aria-label="Services" className={cn('flex flex-col', field ? 'gap-2' : 'gap-2.5')}>
      {field ? (
        <div className="flex items-baseline justify-between gap-2 px-1.5">
          <span className="text-[13px] font-semibold tracking-[0.04em] text-ink-500 uppercase">
            Services
            {services.length > 0 ? ` · ${done} of ${services.length} done` : ''}
          </span>
          {canEdit ? (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="text-[14px] font-semibold text-brand-700"
            >
              Add a service
            </button>
          ) : null}
        </div>
      ) : (
        <div className="flex items-center gap-2.5">
          <Label>Services</Label>
          {services.length > 0 ? (
            <span className="text-[11.5px] text-ink-500">
              {done} of {services.length} done
            </span>
          ) : null}
          {canEdit ? (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="ml-auto text-[13px] font-semibold text-brand-700 hover:text-brand-800"
            >
              Add a service
            </button>
          ) : null}
        </div>
      )}

      {services.length === 0 ? (
        <p
          className={cn(
            'text-ink-500',
            field
              ? 'rounded-[14px] bg-white px-4 py-4 text-[14px] leading-snug ring-1 ring-ink-200 ring-inset'
              : 'rounded-[10px] border border-dashed border-ink-300 px-4 py-5 text-center text-[13px]',
          )}
        >
          {canEdit
            ? 'No services on this job yet. Add the work being done and each one gets its own report.'
            : 'No services on this job yet.'}
          {job.checklists && job.checklists.length > 0
            ? ' What ServiceM8 recorded is in the checklist below.'
            : ''}
        </p>
      ) : (
        <ul className={cn('flex flex-col', field ? 'gap-2.5' : 'gap-2.5')}>
          {services.map((service) => (
            <li key={service.id}>
              <ServiceCard
                service={service}
                progress={reports.data?.progress[service.id]}
                open={open === service.id}
                canEdit={canEdit}
                isAddingNote={isAddingNote}
                field={field}
                onToggle={() => setOpen((current) => (current === service.id ? null : service.id))}
                onAddNote={(body, finished) => onAddNote(service.id, body, finished)}
                onRemove={() => onRemoveService(service.id)}
                {...(onEmailReport ? { onEmailReport } : {})}
              />
            </li>
          ))}
        </ul>
      )}

      {reports.data?.lastVisit ? <LastTimeHere visit={reports.data.lastVisit} field={field} /> : null}

      <AddServiceSheet
        open={adding}
        jobId={job.id}
        already={services.map((service) => service.serviceType.id)}
        onClose={() => setAdding(false)}
      />
    </section>
  )
}

const STAGE_LINE: Record<ReportStage, { dot: string; text: string }> = {
  none: { dot: 'bg-ink-300', text: 'text-ink-500' },
  new: { dot: 'bg-ink-300', text: 'text-ink-500' },
  progress: { dot: 'bg-brand-500', text: 'text-brand-600' },
  done: { dot: 'bg-brand-800', text: 'text-brand-800' },
}

/**
 * One service: what it is, where its report is, and the next thing to do
 * with it.
 *
 * The icon sits on a sky tile in Buster Navy whatever the service, the way the
 * mock-up drew it: the tone colours told six identical grey rows apart, and a
 * card with a status line and its own button no longer needs them to.
 */
function ServiceCard({
  service,
  progress,
  open,
  canEdit,
  isAddingNote,
  field,
  onToggle,
  onAddNote,
  onRemove,
  onEmailReport,
}: {
  service: JobServiceRow
  progress: JobReports['progress'][string] | undefined
  open: boolean
  canEdit: boolean
  isAddingNote: boolean
  field: boolean
  onToggle: () => void
  onAddNote: (body: string, done: () => void) => void
  onRemove: () => void
  onEmailReport?: (pdf: ReportPdf) => void
}) {
  const trpc = useTRPC()
  const { openReport } = useJobOverlay()
  const line = reportLine(service, progress)
  const skin = STAGE_LINE[line.stage]
  const response = service.formResponse
  const subtitle = service.scope?.split('\n')[0]?.trim() || service.serviceType.description || null

  // A signed report's PDF is stored, so this is a lookup; one whose render
  // failed is rendered now, which is how the office gets it (rule 31).
  const [then, setThen] = useState<'open' | 'email'>('open')
  const pdf = useMutation(
    trpc.forms.document.mutationOptions({
      onSuccess: (document) => {
        if (then === 'email' && onEmailReport) {
          onEmailReport({
            attachmentId: document.attachmentId,
            filename: document.filename,
            serviceName: service.serviceType.name,
          })
          return
        }
        const url = downloadUrl({ id: document.attachmentId, checksum: document.checksum })
        if (url) window.open(url, '_blank', 'noopener')
      },
    }),
  )
  const fetchPdf = (next: 'open' | 'email') => {
    if (!response) return
    setThen(next)
    pdf.mutate({ responseId: response.id })
  }

  let actions: ReactNode = null
  if (service.serviceType.formId !== null) {
    if (line.stage === 'done' && response) {
      actions = (
        <>
          <Button
            size="sm"
            icon={<Icon name="terms" className="size-4" />}
            loading={pdf.isPending && then === 'open'}
            onClick={() => fetchPdf('open')}
          >
            PDF
          </Button>
          {onEmailReport ? (
            <Button
              size="sm"
              variant="primary"
              icon={<Icon name="send" className="size-4" />}
              loading={pdf.isPending && then === 'email'}
              onClick={() => fetchPdf('email')}
            >
              Email to client
            </Button>
          ) : null}
        </>
      )
    } else {
      actions = (
        <Button size="sm" variant="primary" onClick={() => openReport(service.id)}>
          {line.stage === 'progress' ? 'Continue report' : 'Start report'}
        </Button>
      )
    }
  }

  return (
    <div
      className={cn(
        'overflow-hidden bg-white ring-1 ring-inset',
        field ? 'rounded-[14px]' : 'rounded-xl',
        open ? 'ring-ink-300' : 'ring-ink-200',
      )}
    >
      <div
        className={cn(
          'grid grid-cols-[40px_minmax(0,1fr)] items-center gap-x-3.5 gap-y-2.5 px-4 py-3.5 sm:grid-cols-[40px_minmax(0,1fr)_auto]',
          open && 'border-b border-ink-100',
        )}
      >
        <span
          aria-hidden
          className="grid size-10 place-items-center rounded-[10px] bg-[color-mix(in_srgb,var(--color-sky)_32%,white)] text-brand-800"
        >
          <Icon name={serviceIcon(service.serviceType.icon)} className="size-5" />
        </span>

        <button
          type="button"
          aria-expanded={open}
          onClick={onToggle}
          className="flex min-w-0 flex-col items-start text-left"
        >
          <span className="max-w-full truncate text-[15px] font-semibold text-ink-900">
            {service.serviceType.name}
          </span>
          {subtitle ? (
            <span className="max-w-full truncate text-[12.5px] text-ink-500" title={subtitle}>
              {subtitle}
            </span>
          ) : null}
          <span className={cn('mt-1.5 inline-flex items-center gap-1.5 text-[12.5px] font-semibold', skin.text)}>
            <span aria-hidden className={cn('size-2 rounded-full', skin.dot)} />
            {line.text}
          </span>
        </button>

        {actions ? (
          <div className="col-span-2 flex flex-wrap justify-end gap-2 sm:col-span-1">{actions}</div>
        ) : null}
      </div>

      {pdf.error ? (
        <p role="alert" className="px-4 pb-3 text-[12.5px] text-red-700">
          {errorMessage(pdf.error)}
        </p>
      ) : null}

      {open ? (
        <div className={cn('flex flex-col gap-3 px-4 pt-3 pb-3.5', field && 'bg-ink-50 text-ink-800')}>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <p className="min-w-0 flex-1 text-[12.5px] leading-relaxed opacity-80">
              {service.scope ?? 'Nobody has written down what this service covers on this job.'}
            </p>
            {service.priceIncTax === null ? null : (
              <span className="text-[13.5px] font-bold tabular-nums">
                {money(service.priceIncTax)}
              </span>
            )}
          </div>

          <Brief jobServiceId={service.id} />

          <ServiceNotes
            service={service}
            canEdit={canEdit}
            isAdding={isAddingNote}
            onAddNote={onAddNote}
          />

          <div className="flex flex-wrap items-center gap-2">
            {service.serviceType.formId === null ? (
              <p className="text-[12.5px] opacity-70">
                This service does not produce a report. Anything worth keeping goes in a note.
              </p>
            ) : line.stage === 'done' ? (
              <Button size="sm" variant="ghost" onClick={() => openReport(service.id)}>
                Read the report back
              </Button>
            ) : null}
            {canEdit ? (
              <button
                type="button"
                onClick={onRemove}
                className="ml-auto text-xs font-medium opacity-70 hover:text-red-700 hover:opacity-100"
              >
                Take this service off the job
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

/**
 * The last visit here that left a report: when, who, and a line per report.
 *
 * The line is the form's `recap` ("no live termites, risk high, 2 things to
 * fix"). A form without one, which is every imported ServiceM8 form, still
 * gets its bullet, saying a report is on file.
 */
function LastTimeHere({ visit, field }: { visit: NonNullable<JobReports['lastVisit']>; field: boolean }) {
  return (
    <section
      aria-label="Last time here"
      className={cn(
        'border border-dashed border-ink-300 bg-ink-50 px-4 py-3.5',
        field ? 'rounded-[14px]' : 'rounded-xl',
      )}
    >
      <h3 className="flex items-center gap-2 text-[13px] font-semibold text-ink-700">
        <Icon name="refresh" className="size-4 text-ink-500" />
        Last time here · {formatDate(visit.at)}
        {visit.by ? ` · ${visit.by}` : ''}
      </h3>
      <ul className="mt-1.5 list-disc pl-5 text-[13px] leading-relaxed text-ink-600">
        {visit.reports.map((report) => (
          <li key={report.responseId}>
            {report.name}: {report.recap ?? 'report on file'}
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * One part of the brief: its heading on the service's colour, its content on
 * paper.
 *
 * The heading inherits the row's tone instead of setting a colour of its own,
 * which is the whole trick — a job with six services reads as six pieces of
 * work rather than six identical grey lists.
 */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[10px] font-bold tracking-[0.06em] uppercase opacity-65">{title}</span>
      <div className="rounded-[9px] bg-white px-3 py-2.5 ring-1 ring-black/5 ring-inset">
        {children}
      </div>
    </div>
  )
}

/**
 * The three things a technician wants before they get out of the ute.
 *
 * One query, three blocks: what the last few people to do this work *at this
 * address* wrote down, what can hurt somebody doing it, and whether the person
 * reading the screen is allowed to. The server scopes all three — history to this
 * service type at this site, hazards to the service type, tickets to the caller
 * — because none of that is the client's business to filter.
 *
 * Fetched when the row opens rather than with the job. Five collapsed rows would
 * otherwise pay for five site-wide note searches to draw five lines of text.
 */
function Brief({ jobServiceId }: { jobServiceId: string }) {
  const trpc = useTRPC()
  const { data, isPending, error } = useQuery(
    trpc.jobs.serviceBrief.queryOptions({ jobServiceId }),
  )

  if (isPending) return <Spinner className="size-4 text-ink-400" />
  if (error) return <p className="text-[12.5px] text-red-700">{errorMessage(error)}</p>

  return (
    <>
      <PreviousVisits history={data.history} />
      <Safety hazards={data.hazards} siteHazardNotes={data.siteHazardNotes} />
      <Tickets tickets={data.tickets} />
    </>
  )
}

/**
 * What the last people to do this work here wrote down.
 *
 * The question a technician has at the gate, and the one that used to mean
 * opening three closed jobs.
 */
function PreviousVisits({ history }: { history: BriefHistory }) {
  return (
    <Section title="Notes from the last visits">
      {history.length === 0 ? (
        <p className="text-[12.5px] text-ink-500">
          Nothing written against this service at this address before. This is the first one.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-ink-100">
          {history.map((note) => (
            <li key={note.id} className="py-2 first:pt-0 last:pb-0">
              <div className="flex items-baseline gap-2">
                <span className="text-[12px] font-semibold text-ink-800">
                  {note.author?.name ?? 'Someone'}
                </span>
                <span className="ml-auto text-[11px] text-ink-500">
                  {note.job?.completedAt ? formatDate(note.job.completedAt) : formatDate(note.createdAt)}
                </span>
              </div>
              <p className="mt-1 text-[13px] leading-relaxed whitespace-pre-wrap text-ink-700">
                {note.body}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}

/**
 * What can hurt somebody doing this work, and what to do about it.
 *
 * Two sources, kept apart on purpose. The hazards belong to the kind of work — a
 * subfloor is a confined space everywhere — and the site's own notes belong to
 * the address, which is where the kelpie and the locked side gate live. Merging
 * them would mean either retyping the confined-space line at four thousand
 * addresses or losing the dog.
 *
 * The critical ones come first, from the server. There are only two levels
 * because a third is where everything becomes "high" and the word stops meaning
 * anything.
 */
function Safety({
  hazards,
  siteHazardNotes,
}: {
  hazards: BriefHazards
  siteHazardNotes: string | null
}) {
  if (hazards.length === 0 && siteHazardNotes === null) return null

  return (
    <Section title="Safety on this service">
      {hazards.length > 0 ? (
        <ul className="flex flex-col gap-2.5">
          {hazards.map((hazard) => (
            <li key={hazard.id} className="flex gap-2.5">
              <span
                aria-hidden
                className={cn(
                  'mt-[7px] size-[7px] shrink-0 rounded-full',
                  hazard.severity === 'CRITICAL' ? 'bg-red-600' : 'bg-amber-500',
                )}
              />
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-[13px] font-semibold text-ink-800">
                  {hazard.title}
                  {hazard.severity === 'CRITICAL' ? (
                    <span className="ml-2 text-[10px] font-bold tracking-wide uppercase text-red-700">
                      Critical
                    </span>
                  ) : null}
                </span>
                <span className="text-[12.5px] leading-relaxed text-ink-600">{hazard.control}</span>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {siteHazardNotes === null ? null : (
        <div
          className={cn(
            'rounded-[7px] bg-amber-50 px-3 py-2.5 ring-1 ring-amber-200 ring-inset',
            hazards.length > 0 && 'mt-2.5',
          )}
        >
          <div className="flex items-center gap-1.5">
            <Icon name="alert" className="size-3.5 text-amber-700" />
            <span className="text-[11px] font-bold tracking-wide uppercase text-amber-800">
              At this address
            </span>
          </div>
          <p className="mt-1 text-[13px] leading-relaxed whitespace-pre-wrap text-ink-800">
            {siteHazardNotes}
          </p>
        </div>
      )}
    </Section>
  )
}

/**
 * Whether the person reading this is allowed to do the work.
 *
 * The caller's own holdings and nobody else's, which is a deliberate limit: a
 * technician looking at their own gaps is a prompt to renew, and looking at a
 * colleague's is a payroll matter that does not belong on a job card.
 *
 * A blocking ticket is drawn as a banner rather than a badge in a list, because
 * the whole point is that it is read before the work starts rather than after.
 * It does not disable the report button. Somebody standing at a gate with a
 * lapsed licence needs to be told, not locked out of the only screen that would
 * let them write down why they left.
 */
function Tickets({ tickets }: { tickets: BriefTickets }) {
  if (tickets.length === 0) return null

  const blocking = tickets.filter((ticket) => ticket.blocking)

  return (
    <Section title="Your tickets for this work">
      {blocking.length > 0 ? (
        <div
          role="alert"
          className="mb-2.5 rounded-[7px] bg-red-50 px-3 py-2.5 ring-1 ring-red-200 ring-inset"
        >
          <div className="flex items-center gap-1.5">
            <Icon name="shield" className="size-3.5 text-red-700" />
            <span className="text-[11px] font-bold tracking-wide uppercase text-red-800">
              {blocking.length === 1 ? 'A ticket this work needs' : 'Tickets this work needs'}
            </span>
          </div>
          <p className="mt-1 text-[13px] leading-relaxed text-ink-800">
            {blocking.map((ticket) => ticket.licenceType.name).join(', ')}
            {blocking.length === 1 ? ' is not current for you.' : ' are not current for you.'} Ring
            the office before you start.
          </p>
        </div>
      ) : null}

      <ul className="flex flex-col divide-y divide-ink-100">
        {tickets.map((ticket) => {
          const badge = ticketBadge(ticket.standing)

          return (
            <li key={ticket.id} className="flex items-center gap-2.5 py-2 first:pt-0 last:pb-0">
              <Icon name="card" className="size-4 shrink-0 text-ink-400" />
              <div className="flex min-w-0 flex-col gap-px">
                <span className="truncate text-[13px] font-semibold text-ink-800">
                  {ticket.licenceType.name}
                  {ticket.isMandatory ? null : (
                    <span className="ml-2 text-[10px] font-medium text-ink-500">Worth having</span>
                  )}
                </span>
                <span className="text-[11.5px] text-ink-500">{ticketDetail(ticket)}</span>
              </div>
              <span
                className={cn(
                  'ml-auto shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold tracking-wide uppercase ring-1 ring-inset',
                  toneStyles[badge.tone],
                )}
              >
                {badge.label}
              </span>
            </li>
          )
        })}
      </ul>
    </Section>
  )
}

/** Notes against this service on this job, and adding one. */
function ServiceNotes({
  service,
  canEdit,
  isAdding,
  onAddNote,
}: {
  service: JobServiceRow
  canEdit: boolean
  isAdding: boolean
  onAddNote: (body: string, done: () => void) => void
}) {
  const [draft, setDraft] = useState('')

  return (
    <div className="flex flex-col gap-2">
      <Section title="Notes on this service">
        {service.notes.length === 0 ? (
          <p className="text-[12.5px] text-ink-500">Nothing noted against this service yet.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-ink-100">
            {service.notes.map((note) => (
              <li key={note.id} className="py-2 first:pt-0 last:pb-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-[12px] font-semibold text-ink-800">
                    {note.author?.name ?? 'Someone'}
                  </span>
                  <span className="ml-auto text-[11px] text-ink-500">
                    {formatDateTime(note.createdAt)}
                  </span>
                </div>
                <p className="mt-1 text-[13px] leading-relaxed whitespace-pre-wrap text-ink-700">
                  {note.body}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {canEdit ? (
        <form
          className="flex flex-col gap-1.5"
          onSubmit={(event) => {
            event.preventDefault()
            const body = draft.trim()
            if (body.length === 0) return
            onAddNote(body, () => setDraft(''))
          }}
        >
          <Textarea
            aria-label={`Note about ${service.serviceType.name}`}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="What should the office or the next technician know about this service?"
            className="min-h-16 text-[13px]"
          />
          <div className="flex justify-end">
            <Button type="submit" loading={isAdding} disabled={draft.trim().length === 0}>
              Add the note
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  )
}
